import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { apiCallWithHost } from '../../utils/apiClient'

// Async thunk for fetching client data
export const fetchClientData = createAsyncThunk(
  'client/fetchClientData',
  async ({ token, externalId, profileId, forceRefresh = false }, { getState }) => {
    const state = getState()
    const clientState = state.client || initialState
    const cachedClient = clientState.dataByProfile[profileId]
    const lastFetched = clientState.lastFetchedByProfile[profileId]

    // If we have cached data and it's less than 5 minutes old and not forcing refresh
    if (!forceRefresh && cachedClient && lastFetched) {
      const fiveMinutesAgo = Date.now() - 5 * 60 * 1000
      if (lastFetched > fiveMinutesAgo) {
        return { data: cachedClient, profileId }
      }
    }

    // Fetch fresh data from API
    const response = await apiCallWithHost(`/clients/${externalId}`, {}, token)

    if (!response.ok) {
      throw new Error('Failed to fetch client data')
    }

    const data = await response.json()
    return { data, profileId }
  },
)

const initialState = {
  dataByProfile: {}, // Map of profileId to client data
  lastFetchedByProfile: {}, // Map of profileId to last fetch timestamp
  statusByProfile: {}, // Map of profileId to status
  errorByProfile: {}, // Map of profileId to error
}

const clientSlice = createSlice({
  name: 'client',
  initialState,
  reducers: {
    clearClientData: (state, action) => {
      const profileId = action.payload
      if (profileId) {
        delete state.dataByProfile[profileId]
        delete state.lastFetchedByProfile[profileId]
        delete state.statusByProfile[profileId]
        delete state.errorByProfile[profileId]
      } else {
        // Clear all data if no profileId provided
        state.dataByProfile = {}
        state.lastFetchedByProfile = {}
        state.statusByProfile = {}
        state.errorByProfile = {}
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClientData.pending, (state, action) => {
        const profileId = action.meta.arg.profileId
        state.statusByProfile[profileId] = 'loading'
      })
      .addCase(fetchClientData.fulfilled, (state, action) => {
        const { data, profileId } = action.payload
        state.statusByProfile[profileId] = 'succeeded'
        state.dataByProfile[profileId] = data
        state.lastFetchedByProfile[profileId] = Date.now()
        state.errorByProfile[profileId] = null
      })
      .addCase(fetchClientData.rejected, (state, action) => {
        const profileId = action.meta.arg.profileId
        state.statusByProfile[profileId] = 'failed'
        state.errorByProfile[profileId] = action.error.message
      })
  },
})

export const { clearClientData } = clientSlice.actions

// Selectors
export const getClientData = (state, profileId) => state.client?.dataByProfile[profileId] ?? null
export const getClientStatus = (state, profileId) =>
  state.client?.statusByProfile[profileId] ?? 'idle'
export const getClientError = (state, profileId) => state.client?.errorByProfile[profileId] ?? null
export const getClientLastFetched = (state, profileId) =>
  state.client?.lastFetchedByProfile[profileId] ?? null

export default clientSlice.reducer
